import { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Globe, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import { useExternalLink } from "@/contexts/ExternalLinkContext";
import { languageFlags, type Language } from "@/i18n/translations";

import logoImg from "@/assets/chibi-logo.webp";

const RESERVE_URL = "https://www.foodbooking.com/ordering/?restaurant_uid=b1c06bc6-d5a3-43d4-82d8-10720b240538&reservation=true";

const subLinks = [
  { to: "/menu", key: "nav_menu" },
  { to: "/galeria", key: "nav_gallery" },
  { to: "/nosotros", key: "nav_about" },
  { to: "/blog", key: "nav_blog" },
  { to: "/restaurante-japones-barcelona", key: "nav_restaurant" },
  { to: "/contacto", key: "nav_contact" },
];

const languages: Language[] = ["es", "en", "ca", "pt", "fr", "it", "de", "ru"];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState(false);
  const megaTimeout = useRef<ReturnType<typeof setTimeout>>();
  const { pathname } = useLocation();
  const { lang, setLang, t } = useLanguage();
  const { openLink } = useExternalLink();

  const handleMegaEnter = () => {
    clearTimeout(megaTimeout.current);
    setMegaOpen(true);
  };
  const handleMegaLeave = () => {
    megaTimeout.current = setTimeout(() => setMegaOpen(false), 200);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-kuro/90 backdrop-blur-md border-b border-shiro/10">
        <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 py-2 md:py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img src={logoImg} alt="Chibikokoro" className="h-10 md:h-12 w-auto" width={48} height={48} />
            <div className="flex flex-col items-start">
              <span className="font-display text-lg md:text-xl font-semibold text-shiro tracking-wider leading-tight">
                <span className="text-aka">CHIBI</span><span className="text-shiro/70">KOKORO</span>
              </span>
              <span className="text-[9px] md:text-[10px] tracking-[0.2em] text-shiro/40 font-body italic">
                Japanese Restaurant
              </span>
            </div>
          </Link>

          {/* Desktop links */}
          <ul className="hidden lg:flex items-center gap-8">
            {/* Inicio with mega dropdown */}
            <li
              className="relative"
              onMouseEnter={handleMegaEnter}
              onMouseLeave={handleMegaLeave}
            >
              <Link
                to="/"
                className={`text-sm tracking-widest uppercase font-body transition-colors duration-300 flex items-center gap-1 ${
                  pathname === "/" ? "text-aka" : "text-shiro/70 hover:text-shiro"
                }`}
              >
                {t("nav_home")}
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${megaOpen ? "rotate-180" : ""}`} />
              </Link>
              <AnimatePresence>
                {megaOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.97 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute top-full left-0 mt-3 bg-kuro/95 backdrop-blur-xl border border-shiro/10 rounded-lg shadow-2xl shadow-black/40 py-3 min-w-[240px]"
                  >
                    {subLinks.map((l) => (
                      <Link
                        key={l.to}
                        to={l.to}
                        onClick={() => setMegaOpen(false)}
                        className={`block px-5 py-2.5 text-sm tracking-widest uppercase font-body transition-colors duration-200 ${
                          pathname === l.to
                            ? "text-aka bg-shiro/5"
                            : "text-shiro/70 hover:text-shiro hover:bg-shiro/5"
                        }`}
                      >
                        {t(l.key)}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </li>

            {/* Regular nav links */}
            {subLinks.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className={`text-sm tracking-widest uppercase font-body transition-colors duration-300 ${
                    pathname === l.to ? "text-aka" : "text-shiro/70 hover:text-shiro"
                  }`}
                >
                  {t(l.key)}
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop CTA + Language */}
          <div className="hidden lg:flex items-center gap-4">
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 text-shiro/60 hover:text-shiro transition-colors text-sm font-body"
                aria-label="Change language"
              >
                <Globe className="w-4 h-4" />
                <span className="text-xs uppercase">{lang}</span>
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="absolute top-full right-0 mt-2 bg-kuro/95 backdrop-blur-lg border border-shiro/10 rounded-lg py-2 min-w-[140px]"
                  >
                    {languages.map((l) => (
                      <button
                        key={l}
                        onClick={() => { setLang(l); setLangOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-sm font-body flex items-center gap-2 hover:bg-shiro/10 transition-colors ${
                          lang === l ? "text-aka" : "text-shiro/70"
                        }`}
                      >
                        <span>{languageFlags[l]}</span>
                        <span className="uppercase text-xs">{l}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <a
              href="tel:+34931463045"
              className="text-shiro/60 hover:text-shiro transition-colors"
              aria-label={t("cta_call")}
            >
              <Phone className="w-4 h-4" />
            </a>
            <button
              onClick={() => openLink(RESERVE_URL)}
              className="bg-primary text-primary-foreground text-xs tracking-widest uppercase px-6 py-2.5 hover:bg-primary/90 transition-colors"
            >
              {t("cta_reserve")}
            </button>
          </div>

          {/* Mobile toggle */}
          <div className="flex items-center gap-3 lg:hidden">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="text-shiro/60 hover:text-shiro p-1"
              aria-label="Change language"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={() => setOpen(!open)}
              className="text-shiro p-2"
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>
      </div>

      {/* Language dropdown mobile */}
      <AnimatePresence>
        {langOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-kuro/95 backdrop-blur-lg border-b border-shiro/10"
          >
            <div className="flex flex-wrap justify-center gap-2 py-4 px-5">
              {languages.map((l) => (
                <button
                  key={l}
                  onClick={() => { setLang(l); setLangOpen(false); }}
                  className={`px-3 py-1.5 text-xs font-body flex items-center gap-1.5 transition-colors ${
                    lang === l ? "text-aka bg-shiro/10" : "text-shiro/60 hover:text-shiro"
                  }`}
                >
                  <span>{languageFlags[l]}</span>
                  <span className="uppercase">{l}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-kuro/95 backdrop-blur-lg border-b border-shiro/10"
          >
            <ul className="flex flex-col items-center py-8 gap-1">
              {/* Inicio accordion */}
              <li className="w-full">
                <button
                  onClick={() => setMobileSubOpen(!mobileSubOpen)}
                  className={`w-full flex items-center justify-center gap-2 py-3 text-sm tracking-widest uppercase font-body ${
                    pathname === "/" ? "text-aka" : "text-shiro/70"
                  }`}
                >
                  {t("nav_home")}
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileSubOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileSubOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col items-center gap-1 pb-3">
                        <Link
                          to="/"
                          onClick={() => { setOpen(false); setMobileSubOpen(false); }}
                          className={`py-2 text-xs tracking-widest uppercase font-body ${
                            pathname === "/" ? "text-aka" : "text-shiro/50 hover:text-shiro/70"
                          }`}
                        >
                          {t("nav_home")}
                        </Link>
                        {subLinks.map((l) => (
                          <Link
                            key={l.to}
                            to={l.to}
                            onClick={() => { setOpen(false); setMobileSubOpen(false); }}
                            className={`py-2 text-xs tracking-widest uppercase font-body ${
                              pathname === l.to ? "text-aka" : "text-shiro/50 hover:text-shiro/70"
                            }`}
                          >
                            {t(l.key)}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>

              {/* Direct nav links */}
              {subLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={`block py-3 text-sm tracking-widest uppercase font-body ${
                      pathname === l.to ? "text-aka" : "text-shiro/70"
                    }`}
                  >
                    {t(l.key)}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  onClick={() => { openLink(RESERVE_URL); setOpen(false); }}
                  className="bg-primary text-primary-foreground text-xs tracking-widest uppercase px-8 py-3 mt-2 inline-block"
                >
                  {t("cta_reserve_table")}
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
